import { useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { giftcardState } from '../client-state/giftcard-state-context';
import useLocale from '../../../Hooks/use-locale';
import useLocalize from '../../../Hooks/useLocalize';
import { capitalize, formatDuration } from '../../../Utils/utils';
import localizations from '../../../localizations';
import usePayment, { PaymentOptions } from '../../../Hooks/usePayment';
import useGiftcardTracking from '../../../Hooks/use-giftcard-tracking';
import { post } from '../../../Utils/fetch';
import { PaymentMethod } from '@stripe/stripe-js';
import useGiftcardPrices from '../../../Hooks/use-giftcard-prices';
import PaymentModal from '../../../ui-components/payment-modal';
import { paymentReducerActionTypes } from '../../../redusers/payment-reducer';
import { vouchersReducerActionTypes } from '../../../redusers/vouchers-reducer';
import { voucherReducerActionTypes } from '../../../redusers/voucher-reducer';
import { errorReducerActionTypes } from '../../../redusers/error-reducer';
import { loadingReducerActionTypes } from '../../../redusers/loading-reducer';
import { isSummaryOpenActionTypes } from '../../../redusers/is-summary-open-reducer';
import { Elements } from '@stripe/react-stripe-js';
import useGiftcardShop from '../server-data/use-giftcard-shop';
import useStripeObject from '../../../Hooks/use-stripe-object';
import useGiftcardMinimalShop from '../server-data/use-giftcard-minimal-shop';

const PaymentModalWithDataAndStripe = () => {
  const { data: minimalShop } = useGiftcardMinimalShop();
  const { data: shop } = useGiftcardShop();
  const { state, dispatch } = useContext(giftcardState);
  const { languageAsEnum } = useLocale();
  const localization = localizations[languageAsEnum];
  const localize = useLocalize();
  const { handlePayment } = usePayment();
  const track = useGiftcardTracking();
  const [cookies] = useCookies(['goboat_affiliate']);
  const { giftcardPrices } = useGiftcardPrices();

  const summaryDurationString = shop?.configuration?.hideGiftCardDurations
    ? ''
    : ` • ${formatDuration(state.giftcard.duration, { localization })}`;

  const paymentRequestItems = useMemo(() => {
    const paymentItems = [];

    if (giftcardPrices.rentalPrice) {
      const amount = giftcardPrices.rentalPrice;
      const label = state.giftcard.campaignName
        ? state.giftcard.campaignName
        : `${localize('product.Giftcard')}${summaryDurationString}}`;

      paymentItems.push({ amount, label });
    }

    if (giftcardPrices.voucherValue && giftcardPrices.voucherValue > 0) {
      const amount = -giftcardPrices.voucherValue;
      const label = capitalize(
        state.vouchers[0]?.category
          ? localize(`product.${state.vouchers[0].category}`)
          : localize('product.giftcard')
      );

      paymentItems.push({ amount, label });
    }

    return paymentItems;
  }, [
    summaryDurationString,
    state.giftcard.campaignName,
    giftcardPrices,
    state.vouchers,
    localize,
  ]);

  const paymentRequestObject = useMemo(() => {
    if (minimalShop?.currency && giftcardPrices.toBePaid) {
      return {
        country: 'DK',
        currency: String(minimalShop?.currency).toLowerCase(),
        total: {
          label: localize('price.allIncluded'),
          amount: giftcardPrices.toBePaid,
        },
        displayItems: paymentRequestItems,
        requestPayerName: true,
        requestPayerEmail: true,
      };
    }
  }, [minimalShop?.currency, giftcardPrices.toBePaid, localize, paymentRequestItems]);

  const handleNegativeBalance = async (paymentMethod?: PaymentMethod) => {
    if (giftcardPrices.toBePaid && minimalShop?.currency) {
      const endpoint = `Giftcards/purchase`;
      const options: PaymentOptions = {
        customer: state.customer.customer,
        amount: giftcardPrices.toBePaid,
        currency: minimalShop?.currency,
        duration: state.giftcard.duration,
        value: giftcardPrices.rentalPrice,
        shopId: state.giftcard.shopId,
        lang: state.giftcard.lang,
        recipient: { name: state.recipient.name },
        voucherCode: state.vouchers[0]?.code,
        type: state.giftcard.type,
        newsletterSubscribed: state.terms.newsletterSubscribed ?? false,
      };

      // Check for affiliate cookie
      if (cookies.goboat_affiliate) options.affiliate = cookies.goboat_affiliate;

      try {
        dispatch({ type: paymentReducerActionTypes.UPDATE_IS_LOADING, payload: true });
        const giftcard = await handlePayment(endpoint, options, paymentMethod);
        dispatch({ type: paymentReducerActionTypes.UPDATE_IS_LOADING, payload: false });

        track({ type: 'TRANSACTION_COMPLETE_GIFTCARD', product: giftcard });

        return dispatch({
          type: paymentReducerActionTypes.UPDATE_IS_PAID,
          payload: true,
        });
      } catch (error: any) {
        dispatch({ type: paymentReducerActionTypes.UPDATE_IS_LOADING, payload: false });
        dispatch({ type: paymentReducerActionTypes.UPDATE_UUID });

        return dispatch({
          type: errorReducerActionTypes.PAYMENT,
          payload: error.message,
        });
      }
    }
  };

  const handleZeroBalance = async () => {
    const endpoint = `Giftcards/purchase`;
    const options = {
      customer: state.customer.customer,
      amount: giftcardPrices.toBePaid,
      currency: minimalShop?.currency,
      duration: state.giftcard.duration,
      value: giftcardPrices.rentalPrice,
      shopId: state.giftcard.shopId,
      lang: state.giftcard.lang,
      recipient: { name: state.recipient.name },
      voucherCode: state.vouchers[0]?.code,
      type: state.giftcard.type,
      newsletterSubscribed: state.terms.newsletterSubscribed,
    };

    try {
      dispatch({ type: paymentReducerActionTypes.UPDATE_IS_LOADING, payload: true });
      const giftcard = await post(endpoint, options);
      dispatch({ type: paymentReducerActionTypes.UPDATE_IS_LOADING, payload: false });

      track({ type: 'TRANSACTION_COMPLETE_GIFTCARD', product: giftcard });

      return dispatch({ type: paymentReducerActionTypes.UPDATE_IS_PAID, payload: true });
    } catch (error: any) {
      dispatch({ type: paymentReducerActionTypes.UPDATE_IS_LOADING, payload: false });
      dispatch({ type: paymentReducerActionTypes.UPDATE_UUID });

      return dispatch({ type: errorReducerActionTypes.PAYMENT, payload: error.message });
    }
  };

  const applyVoucher = async (voucherCode: string) => {
    const endpoint = `Giftcards/validate-voucher`;
    const options = { voucherCode, shopId: shop?.id };

    dispatch({ type: errorReducerActionTypes.VOUCHER, payload: '' });

    try {
      dispatch({ type: loadingReducerActionTypes.VOUCHERS, payload: true });
      const voucher = await post(endpoint, options);

      dispatch({ type: voucherReducerActionTypes.UPDATE_IS_OPEN, payload: false });
      return dispatch({
        type: vouchersReducerActionTypes.UPDATE_VOUCHERS,
        payload: [voucher],
      });
    } catch (error: any) {
      const errorMessage = localize(`error.${error.message}`, error.message);

      return dispatch({ type: errorReducerActionTypes.VOUCHER, payload: errorMessage });
    }
  };

  return (
    <PaymentModal
      customer={state.customer.customer}
      recipient={state.recipient}
      handleNegativeBalance={handleNegativeBalance}
      handleZeroBalance={handleZeroBalance}
      handlePositiveBalance={handleZeroBalance}
      onPaymentMethodComplete={(paymentMethod = 'card') => {
        track({
          type: 'CHECKOUT_GIFTCARD',
          product: state.giftcard,
          step: 5,
          paymentType: paymentMethod,
        });
      }}
      isLoading={state.payment.isLoading}
      error={state.errors.payment ?? undefined}
      voucher={{
        isOpen: state.voucher.isOpen,
        onOpen: (open) =>
          dispatch({ type: voucherReducerActionTypes.UPDATE_IS_OPEN, payload: open }),
        code: state.voucher.code,
        onChange: (event) =>
          dispatch({
            type: voucherReducerActionTypes.UPDATE_CODE,
            payload: event.target.value,
          }),
        vouchers: state.vouchers,
        applyVoucher: () => applyVoucher(state.voucher.code),
        error: state.errors.voucher ?? undefined,
        loading: state.loading.voucher,
      }}
      paymentRequestObject={paymentRequestObject}
      shop={shop}
      prices={giftcardPrices}
      setSummaryOpen={(open) =>
        dispatch({ type: isSummaryOpenActionTypes.SET_IS_OPEN, payload: open })
      }
      setShowPaymentModal={(show) =>
        dispatch({
          type: paymentReducerActionTypes.UPDATE_SHOW_PAYMENT_MODAL,
          payload: show,
        })
      }
      setError={(error) =>
        dispatch({ type: errorReducerActionTypes.PAYMENT, payload: error ?? '' })
      }
    />
  );
};

const PaymentModalWithData = () => {
  const stripe = useStripeObject();

  return (
    <Elements stripe={stripe ?? null}>
      <PaymentModalWithDataAndStripe />
    </Elements>
  );
};

export default PaymentModalWithData;
